/* ================================================================
   YOLIV HEALTHY — JavaScript
   ================================================================ */
(function() {
  'use strict';

  /* ---- SCROLL REVEAL ---- */
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        e.target.classList.add('in');
        revealObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -50px 0px' });

  document.querySelectorAll('.reveal, .reveal-left, .reveal-right').forEach(el => revealObserver.observe(el));

  /* ---- HEADER SCROLL + STICKY HEIGHT ---- */
  const header = document.querySelector('.site-header');
  const stickyTop = document.querySelector('.yoliv-sticky-top');
  function syncHeaderHeight() {
    if (!stickyTop) return;
    const h = stickyTop.getBoundingClientRect().height;
    if (h > 0) document.documentElement.style.setProperty('--yoliv-header-h', h + 'px');
  }
  syncHeaderHeight();
  window.addEventListener('load', syncHeaderHeight);
  window.addEventListener('resize', syncHeaderHeight, { passive: true });
  if (window.ResizeObserver && stickyTop) {
    new ResizeObserver(syncHeaderHeight).observe(stickyTop);
  }
  window.addEventListener('scroll', () => {
    const scrolled = window.scrollY > 40;
    if (header) header.classList.toggle('scrolled', window.scrollY > 80);
    if (stickyTop) stickyTop.classList.toggle('is-scrolled', scrolled);
  }, { passive: true });

  /* ---- MOBILE MENU ---- */
  const hamburger = document.querySelector('.hamburger');
  const mobileNav = document.querySelector('.mobile-nav');
  const mobileClose = document.querySelector('.mobile-nav-close');
  if (hamburger && mobileNav) {
    hamburger.addEventListener('click', () => {
      mobileNav.classList.add('open');
      document.body.style.overflow = 'hidden';
    });
  }
  if (mobileClose && mobileNav) {
    mobileClose.addEventListener('click', () => {
      mobileNav.classList.remove('open');
      document.body.style.overflow = '';
    });
  }

  /* ---- CART DRAWER ---- */
  const overlay = document.getElementById('cartOverlay');
  const drawer = document.getElementById('cartDrawer');
  const cartClose = document.getElementById('cartClose');

  function cartOutsideClick(e) {
    if (!drawer) return;
    if (drawer.contains(e.target)) return;          // click inside drawer → ignore
    if (e.target.closest('[data-open-cart]')) return; // click on the cart icon → ignore
    closeCart();
  }
  function cartEscClose(e) {
    if (e.key === 'Escape') closeCart();
  }

  window.openCart = function() {
    if (overlay) overlay.classList.add('open');
    if (drawer)  drawer.classList.add('open');
    document.body.style.overflow = 'hidden';
    // attach on next tick so the opening click itself isn't caught
    setTimeout(function () {
      document.addEventListener('mousedown', cartOutsideClick);
      document.addEventListener('touchstart', cartOutsideClick, { passive: true });
      document.addEventListener('keydown', cartEscClose);
    }, 0);
    fetchCart();
  };
  window.closeCart = function() {
    if (overlay) overlay.classList.remove('open');
    if (drawer)  drawer.classList.remove('open');
    document.body.style.overflow = '';
    document.removeEventListener('mousedown', cartOutsideClick);
    document.removeEventListener('touchstart', cartOutsideClick);
    document.removeEventListener('keydown', cartEscClose);
  };
  if (overlay) overlay.addEventListener('click', closeCart);
  if (cartClose) cartClose.addEventListener('click', closeCart);

  document.querySelectorAll('[data-open-cart]').forEach(btn => {
    btn.addEventListener('click', (e) => { e.preventDefault(); openCart(); });
  });

  /* ---- FETCH & RENDER CART ---- */
  // Cache compare_at_price per variant ID so we don't re-fetch repeatedly
  const _compareCache = {};

  async function enrichWithMRP(cart) {
    if (!cart || !cart.items || !cart.items.length) return cart;
    const handles = [...new Set(cart.items.map(i => i.handle).filter(Boolean))];
    const toFetch = handles.filter(h => !(h in _compareCache));
    if (toFetch.length) {
      await Promise.all(toFetch.map(h =>
        fetch('/products/' + h + '.js')
          .then(r => r.json())
          .then(p => {
            _compareCache[h] = {};
            if (p && Array.isArray(p.variants)) {
              p.variants.forEach(v => { _compareCache[h][v.id] = v.compare_at_price; });
            }
          })
          .catch(() => { _compareCache[h] = {}; })
      ));
    }
    cart.items.forEach(item => {
      const map = _compareCache[item.handle];
      if (map && map[item.variant_id]) item.compare_at_price = map[item.variant_id];
    });
    return cart;
  }

  // Strip legacy "Purchase Type" / "Subscription Frequency" properties from cart items
  // (left over from the removed subscription widget). Only runs if any item still has them.
  async function stripLegacySubscriptionProps(cart) {
    if (!cart || !cart.items || !cart.items.length) return cart;
    const stale = cart.items.filter(i =>
      i.properties && (
        i.properties['Purchase Type'] !== undefined ||
        i.properties['Subscription Frequency'] !== undefined
      )
    );
    if (!stale.length) return cart;
    // Build a properties object that explicitly nulls out the two legacy keys.
    // Shopify /cart/change.js with `properties` REPLACES the whole map for that line.
    for (const item of stale) {
      const newProps = Object.assign({}, item.properties);
      delete newProps['Purchase Type'];
      delete newProps['Subscription Frequency'];
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: newProps })
        });
      } catch(e) {}
    }
    // Re-fetch fresh cart after cleanup
    const refreshed = await fetch('/cart.js').then(r => r.json()).catch(() => cart);
    return refreshed;
  }

  function fetchCart() {
    return fetch('/cart.js')
      .then(res => res.json())
      .then(cart => stripLegacySubscriptionProps(cart))
      .then(cart => enrichWithMRP(cart))
      .then(cart => {
        renderCart(cart);
        updateCartBadge(cart.item_count);
        return cart;
      })
      .catch(err => { if (window.YOLIV && window.YOLIV.debug) console.error('Cart fetch error:', err); });
  }

  function escapeHtml(s){ if(s==null) return ''; return String(s).replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c])); }

  function renderCart(cart) {
    const body = document.getElementById('cartDrawerBody');
    const footer = document.getElementById('cartDrawerFooter');
    const count = document.getElementById('drawerCount');
    const subtotal = document.getElementById('cartSubtotal');
    const checkoutTotal = document.getElementById('cartCheckoutTotal');
    const savingsRow = document.getElementById('cartSavingsRow');
    const savingsAmt = document.getElementById('cartSavingsAmt');
    const freeShipText = document.getElementById('cartFreeShipText');
    const freeShipFill = document.getElementById('cartFreeShipFill');

    if (count) count.textContent = cart.item_count;
    if (!body) return;

    // Empty state
    if (cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-empty-state">
          <div class="cart-empty-icon">🫒</div>
          <h4>Your cart is feeling light</h4>
          <p>Add a bottle of cold-pressed Spanish olive oil and start cooking healthier today.</p>
          <a href="/collections/all" class="btn-primary btn-full" data-close-cart>Shop Yoliv Healthy</a>
        </div>`;
      if (footer) footer.style.display = 'none';
      return;
    }

    // Items
    body.innerHTML = cart.items.map(item => {
      // Prefer compare_at_price (true MRP) over original_line_price for savings display.
      // compare_at_price is per-unit; multiply by quantity for the line MRP.
      const compareAt = (item.compare_at_price || item.variant_compare_at_price || 0);
      const line = item.final_line_price;
      const mrp = (compareAt && compareAt > item.final_price) ? (compareAt * item.quantity) : item.original_line_price;
      const hasDisc = mrp > line;
      const variantText = (item.variant_title && item.variant_title !== 'Default Title') ? `<div class="cart-item__variant">${escapeHtml(item.variant_title)}</div>` : '';
      return `
        <div class="cart-item" data-key="${escapeHtml(item.key)}">
          <a class="cart-item__media" href="${escapeHtml(item.url)}">
            ${item.image ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="lazy" width="80" height="80">` : ''}
          </a>
          <div class="cart-item__details">
            <a href="${escapeHtml(item.url)}" class="cart-item__title">${escapeHtml(item.product_title)}</a>
            ${variantText}
            <div class="cart-item__bottom">
              <div class="cart-item__qty">
                <button type="button" class="cart-item__qty-btn" data-cart-qty="minus" aria-label="Decrease quantity">−</button>
                <span class="cart-item__qty-val">${item.quantity}</span>
                <button type="button" class="cart-item__qty-btn" data-cart-qty="plus" aria-label="Increase quantity">+</button>
              </div>
              <div class="cart-item__pricing">
                ${hasDisc ? `<span class="cart-item__mrp">${formatMoney(mrp)}</span>` : ''}
                <span class="cart-item__price">${formatMoney(line)}</span>
              </div>
            </div>
            <button type="button" class="cart-item__remove" data-cart-remove aria-label="Remove">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
              Remove
            </button>
          </div>
        </div>`;
    }).join('');

    if (footer) footer.style.display = 'flex';
    if (subtotal) subtotal.textContent = formatMoney(cart.total_price);
    if (checkoutTotal) checkoutTotal.textContent = formatMoney(cart.total_price);

    // Savings — calculate from MRP (compare_at_price) when available
    const totalSavings = cart.items.reduce((sum, i) => {
      const cmp = i.compare_at_price || i.variant_compare_at_price || 0;
      const lineMrp = (cmp && cmp > i.final_price) ? (cmp * i.quantity) : i.original_line_price;
      return sum + Math.max(0, lineMrp - i.final_line_price);
    }, 0);
    if (savingsRow && savingsAmt) {
      if (totalSavings > 0) {
        savingsRow.removeAttribute('hidden');
        savingsRow.style.display = '';
        savingsAmt.textContent = formatMoney(totalSavings);
      } else {
        savingsRow.setAttribute('hidden', '');
        savingsRow.style.display = 'none';
      }
    }

    // Free shipping progress
    const threshold = (window.YOLIV && window.YOLIV.free_shipping_threshold) || 99900;
    if (freeShipText) {
      if (cart.total_price >= threshold) {
        freeShipText.innerHTML = '<span class="cart-freeship__icn">🎉</span> You unlocked <strong>FREE shipping!</strong>';
      } else {
        const remaining = formatMoney(threshold - cart.total_price);
        freeShipText.innerHTML = `🚚 Add <strong>${remaining}</strong> more for <strong>FREE shipping</strong>`;
      }
    }
    if (freeShipFill) {
      const pct = Math.min(100, Math.round((cart.total_price / threshold) * 100));
      freeShipFill.style.width = pct + '%';
    }
  }

  // QUANTITY UPDATE (delegated)
  function changeQuantity(key, qty) {
    showCartLoading(true);
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(r => r.json())
      .then(() => fetchCart())
      .catch(() => {})
      .finally(() => showCartLoading(false));
  }
  function showCartLoading(on) {
    const el = document.getElementById('cartLoading');
    if (el) el.hidden = !on;
  }

  // Delegated click handlers inside the cart drawer
  document.addEventListener('click', function(e) {
    const qtyBtn = e.target.closest('[data-cart-qty]');
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (qtyBtn) {
      const item = qtyBtn.closest('.cart-item');
      if (!item) return;
      const key = item.dataset.key;
      const valEl = item.querySelector('.cart-item__qty-val');
      let qty = parseInt(valEl ? valEl.textContent : '1', 10) || 1;
      qty = qtyBtn.dataset.cartQty === 'plus' ? qty + 1 : Math.max(0, qty - 1);
      changeQuantity(key, qty);
    } else if (removeBtn) {
      const item = removeBtn.closest('.cart-item');
      if (!item) return;
      changeQuantity(item.dataset.key, 0);
    }
  });

  // Coupon code apply (uses Shopify discount auto-apply URL)
  const couponForm = document.getElementById('cartCouponForm');
  if (couponForm) {
    couponForm.addEventListener('submit', function(e) {
      e.preventDefault();
      const inputEl = document.getElementById('cartCouponInput');
      const msgEl = document.getElementById('cartCouponMsg');
      const code = (inputEl && inputEl.value || '').trim();
      if (!code) return;
      if (msgEl) { msgEl.hidden = false; msgEl.textContent = 'Applying...'; msgEl.className = 'cart-coupon__msg'; }
      // Redirect to /discount/CODE which applies code and returns to checkout-able cart
      window.location.href = '/discount/' + encodeURIComponent(code) + '?redirect=/checkout';
    });
  }

  // Order notes - save on blur
  const notesEl = document.getElementById('cartNotes');
  if (notesEl) {
    notesEl.addEventListener('blur', function() {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: notesEl.value })
      }).catch(() => {});
    });
  }

  // Back-compat
  window.removeItem = function(key) { return changeQuantity(key, 0); };

  function formatMoney(cents){
    const cfg = (window.YOLIV && window.YOLIV.money_format) || '₹{{amount}}';
    const value = (cents/100);
    const formatted = value.toLocaleString((window.YOLIV && window.YOLIV.iso_code) || undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const noDec = Math.round(value).toLocaleString((window.YOLIV && window.YOLIV.iso_code) || undefined);
    return cfg
      .replace(/\{\{\s*amount\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDec)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDec);
  }

  function updateCartBadge(count) {
    document.querySelectorAll('.cart-badge').forEach(b => {
      b.textContent = count;
      b.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* ---- ADD TO CART ---- */
  async function addToCart(variantId, qty, btn) {
    if (!variantId) return;
    const orig = btn ? btn.innerHTML : '';
    if (btn) { btn.classList.add('loading'); btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" style="animation:spin .6s linear infinite"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>`; }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(variantId, 10), quantity: qty || 1 })
      });
      const data = await res.json();
      if (data.id) {
        if (btn) {
          btn.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>`;
          btn.style.background = '#4a7c42';
          setTimeout(() => { btn.innerHTML = orig; btn.style.background = ''; btn.classList.remove('loading'); }, 1800);
        }
        await fetchCart();
        openCart();
      }
    } catch (err) {
      console.error('Add to cart error:', err);
      if (btn) { btn.classList.remove('loading'); btn.innerHTML = orig; }
    }
  }

  document.querySelectorAll('[data-add-to-cart]').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const variantId = btn.dataset.variantId || document.querySelector('[data-variant-id]')?.dataset.variantId;
      const qtyEl = document.querySelector('.qty-val'); const qty = parseInt(qtyEl?.value || qtyEl?.textContent, 10) || 1;
      addToCart(variantId, qty, btn);
    });
  });

  /* ---- QUANTITY STEPPERS ---- */
  document.querySelectorAll('.qty-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const display = btn.closest('.qty-stepper')?.querySelector('.qty-val');
      if (!display) return;
      let val = parseInt(display.value || display.textContent, 10) || 1;
      if (btn.dataset.dir === 'up') val = Math.min(val + 1, 99);
      else val = Math.max(val - 1, 1);
      if (display.tagName === 'INPUT') {
        display.value = val;
        display.dispatchEvent(new Event('input', { bubbles: true }));
      } else {
        display.textContent = val;
      }
    });
  });

  /* ---- VARIANT PILLS ---- */
  document.querySelectorAll('.variant-pills').forEach(group => {
    group.querySelectorAll('.variant-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        group.querySelectorAll('.variant-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        const variantId = pill.dataset.variantId;
        const priceEl = document.querySelector('[data-current-price]');
        const price = pill.dataset.price;
        if (priceEl && price) priceEl.textContent = formatMoney(parseInt(price, 10));
        const atcBtn = document.querySelector('[data-add-to-cart]');
        if (atcBtn && variantId) atcBtn.dataset.variantId = variantId;
      });
    });
  });

  /* ---- PRODUCT IMAGE GALLERY ---- */
  const mainImg = document.querySelector('.product-gallery-main img');
  document.querySelectorAll('.product-gallery-thumb').forEach(thumb => {
    thumb.addEventListener('click', () => {
      document.querySelectorAll('.product-gallery-thumb').forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      if (mainImg) {
        const newSrc = thumb.querySelector('img')?.src;
        if (newSrc) { mainImg.style.opacity = '0'; setTimeout(() => { mainImg.src = newSrc; mainImg.style.opacity = '1'; }, 200); }
      }
    });
  });

  /* ---- PRODUCT ACCORDIONS ---- */
  document.querySelectorAll('.accordion-trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.accordion-item');
      const isOpen = item.classList.contains('open');
      // Close all open items and update their ARIA + hidden state
      document.querySelectorAll('.accordion-item.open').forEach(o => {
        o.classList.remove('open');
        const t = o.querySelector('.accordion-trigger');
        if (t) {
          t.setAttribute('aria-expanded', 'false');
          const panelId = t.getAttribute('aria-controls');
          if (panelId) {
            const panel = document.getElementById(panelId);
            if (panel) panel.setAttribute('hidden', '');
          }
        }
      });
      if (!isOpen) {
        item.classList.add('open');
        trigger.setAttribute('aria-expanded', 'true');
        const panelId = trigger.getAttribute('aria-controls');
        if (panelId) {
          const panel = document.getElementById(panelId);
          if (panel) panel.removeAttribute('hidden');
        }
      }
    });
  });

  /* ---- PRODUCT TABS ---- */
  document.querySelectorAll('.tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.tab;
      document.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.tab-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      const panel = document.getElementById(target);
      if (panel) panel.classList.add('active');
    });
  });

  /* ---- INITIAL CART COUNT + AUTO-CLEAN LEGACY SUBSCRIPTION PROPERTIES ---- */
  fetch('/cart.js')
    .then(r => r.json())
    .then(cart => stripLegacySubscriptionProps(cart))
    .then(cart => updateCartBadge(cart.item_count))
    .catch(() => {});

  /* ---- SPIN ANIMATION ---- */
  const style = document.createElement('style');
  style.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(style);

  /* ---- FAQ ACCORDION ---- */
  document.querySelectorAll('.faq-sidebar-link').forEach(link => {
    link.addEventListener('click', () => {
      const target = link.dataset.target;
      document.querySelectorAll('.faq-sidebar-link').forEach(l => l.classList.remove('active'));
      link.classList.add('active');
      const section = document.getElementById(target);
      if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  /* ---- NEWSLETTER FORM ---- */
  const nlForm = document.querySelector('.newsletter-form');
  if (nlForm) {
    nlForm.addEventListener('submit', e => {
      e.preventDefault();
      const input = nlForm.querySelector('input[type="email"]');
      const note = document.querySelector('.newsletter-note');
      if (input?.value && note) {
        note.textContent = '✓ You\'re subscribed! Welcome to the Yoliv family.';
        note.style.color = 'var(--forest)';
        input.value = '';
      }
    });
  }

  /* ---- SMOOTH ANCHOR SCROLL ---- */
  document.querySelectorAll('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const href = a.getAttribute('href');
      if (!href || href === '#' || href === '#!' || href.length < 2) return;
      const target = document.querySelector(href);
      if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    });
  });

  /* ---- DATA-CLOSE-CART DELEGATION ---- */
  document.addEventListener('click', e => {
    const t = e.target.closest('[data-close-cart]');
    if (t && typeof window.closeCart === 'function') { e.preventDefault(); window.closeCart(); }
  });

  /* ---- VARIANT SWITCHER ---- */
  (function initVariantSwitcher() {
    const form = document.querySelector('.product-form');
    if (!form) return;
    const jsonEl = form.querySelector('[data-product-json]');
    if (!jsonEl) return;
    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    if (!product || !product.variants) return;

    const idInput = form.querySelector('[data-product-id-input]');
    const qtyInput = form.querySelector('[data-qty-input]');
    const buyNow = document.querySelector('[data-buy-now]');
    const priceEl = document.querySelector('[data-current-price]');
    const atcBtn = form.querySelector('[data-add-to-cart]');
    const pillGroups = form.querySelectorAll('.variant-pills');

    function getSelectedOptions() {
      const opts = [];
      pillGroups.forEach(group => {
        const active = group.querySelector('.variant-pill.active');
        if (active) opts.push(active.dataset.value);
      });
      return opts;
    }

    function findVariant(selected) {
      return product.variants.find(v => {
        const vOpts = [v.option1, v.option2, v.option3].filter(o => o !== null && o !== undefined);
        if (vOpts.length !== selected.length) return false;
        return selected.every((s, i) => vOpts[i] === s);
      });
    }

    function getQty() {
      const q = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      return q > 0 ? q : 1;
    }

    function update() {
      const selected = getSelectedOptions();
      const variant = findVariant(selected);

      if (!variant) {
        if (atcBtn) { atcBtn.disabled = true; atcBtn.textContent = 'Unavailable'; }
        if (buyNow) { buyNow.setAttribute('aria-disabled', 'true'); buyNow.style.opacity = '.5'; buyNow.style.cursor = 'not-allowed'; buyNow.removeAttribute('href'); }
        return;
      }

      if (idInput) idInput.value = variant.id;
      if (atcBtn) atcBtn.dataset.variantId = variant.id;
      if (buyNow) {
        buyNow.dataset.variantId = variant.id;
        if (variant.available) {
          buyNow.setAttribute('href', '/cart/' + variant.id + ':' + getQty() + '?return_to=/checkout');
          buyNow.removeAttribute('aria-disabled');
          buyNow.style.opacity = '';
          buyNow.style.cursor = '';
        } else {
          buyNow.setAttribute('aria-disabled', 'true');
          buyNow.style.opacity = '.5';
          buyNow.style.cursor = 'not-allowed';
          buyNow.removeAttribute('href');
        }
      }
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      if (atcBtn) {
        const orig = atcBtn.dataset.originalLabel || 'Add to Cart';
        if (!variant.available) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Sold Out';
        } else {
          atcBtn.disabled = false;
          atcBtn.textContent = orig;
        }
      }

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url.toString());
      } catch (e) {}
    }

    pillGroups.forEach(group => {
      group.querySelectorAll('.variant-pill').forEach(pill => {
        pill.addEventListener('click', () => {
          group.querySelectorAll('.variant-pill').forEach(p => p.classList.remove('active'));
          pill.classList.add('active');
          update();
        });
      });
    });

    if (qtyInput) qtyInput.addEventListener('input', update);
  })();

})();

/* ---- DIALOG ACCESSIBILITY (focus trap, aria sync, Escape) ---- */
(function(){
  function focusableSelector(){ return 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'; }
  function trapFocus(dialog, e){
    if (e.key !== 'Tab') return;
    const focusables = dialog.querySelectorAll(focusableSelector());
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length-1];
    if (e.shiftKey && document.activeElement === first){ last.focus(); e.preventDefault(); }
    else if (!e.shiftKey && document.activeElement === last){ first.focus(); e.preventDefault(); }
  }
  const dialogs = [
    { dialog: '#cartDrawer', opener: '[data-open-cart], .header-cart, .cart-icon', openClass: 'open' },
    { dialog: '#mobileNav', opener: '.hamburger', openClass: 'open' }
  ];
  let lastOpener = null;
  dialogs.forEach(cfg => {
    const dialog = document.querySelector(cfg.dialog);
    if (!dialog) return;
    const observer = new MutationObserver(() => {
      const isOpen = dialog.classList.contains(cfg.openClass);
      dialog.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      document.querySelectorAll(cfg.opener).forEach(b => b.setAttribute('aria-expanded', isOpen ? 'true' : 'false'));
      if (isOpen){
        lastOpener = document.activeElement;
        const f = dialog.querySelector(focusableSelector());
        if (f) setTimeout(() => f.focus(), 50);
      } else if (lastOpener){
        lastOpener.focus();
      }
    });
    observer.observe(dialog, { attributes: true, attributeFilter: ['class'] });
    dialog.addEventListener('keydown', e => trapFocus(dialog, e));
  });
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape') return;
    const openCart = document.querySelector('#cartDrawer.open');
    const openNav = document.querySelector('#mobileNav.open');
    if (openCart && typeof window.closeCart === 'function') window.closeCart();
    if (openNav) openNav.classList.remove('open');
  });
})();
